import React, { useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusinessTypes } from '../hooks/useBusinessTypes';
import { useLanguage } from '../contexts/LanguageContext';
import './BusinessTypesSlider.css';

function formatBusinessTypeName(item, language, t) {
  const formatName = (str) => {
    if (!str || typeof str !== 'string') return '';
    return str
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (language === 'uz') {
    return item.name_uz || item.name || '';
  }
  if (language === 'ru') {
    return item.name_ru || item.name || '';
  }
  if (language === 'en') {
    if (item.nameKey) {
      const translated = t(item.nameKey);
      if (translated && translated !== item.nameKey) {
        return translated;
      }
      return formatName(item.nameKey);
    }
    return formatName(item.name);
  }
  return item.name_oz || item.name_uz || item.name || '';
}

const BusinessTypesSlider = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { list, loading } = useBusinessTypes();
  const trackRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  const items = list.length > 0 ? list : [];

  const applyMarqueeDuration = useCallback(() => {
    requestAnimationFrame(() => {
      const track = trackRef.current;
      if (!track || items.length === 0) return;
      const half = track.scrollWidth / 2;
      if (half <= 0) return;
      const pxPerSec = 48;
      const sec = Math.max(22, Math.min(95, half / pxPerSec));
      track.style.setProperty('--marquee-duration', `${sec}s`);
    });
  }, [items.length]);

  useLayoutEffect(() => {
    applyMarqueeDuration();
    const track = trackRef.current;
    let ro;
    if (typeof ResizeObserver !== 'undefined' && track) {
      ro = new ResizeObserver(() => {
        applyMarqueeDuration();
      });
      ro.observe(track);
    }
    window.addEventListener('resize', applyMarqueeDuration);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', applyMarqueeDuration);
    };
  }, [applyMarqueeDuration, items.length]);

  const handleClick = useCallback(() => {
    navigate('/contact');
  }, [navigate]);

  if (loading) {
    return (
      <div className="business-types-slider-wrap business-types-slider-wrap--skeleton" aria-hidden="true">
        <div className="business-types-slider-skeleton-track">
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="business-types-slider-skeleton-chip" />
          ))}
        </div>
      </div>
    );
  }

  if (items.length === 0) return null;

  const renderSet = (dupIdx) =>
    items.map((item, idx) => (
      <button
        key={`marq-${dupIdx}-${item.id}-${idx}`}
        type="button"
        className="business-types-slider-item"
        tabIndex={dupIdx > 0 ? -1 : 0}
        onClick={handleClick}
      >
        <span className="business-types-slider-item-text">
          {formatBusinessTypeName(item, language, t)}
        </span>
      </button>
    ));

  return (
    <div
      className="business-types-slider-wrap"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setIsPaused(false);
        }
      }}
    >
      <div className="business-types-slider-wrapper">
        <div
          ref={trackRef}
          className="business-types-slider-track"
          style={{ animationPlayState: isPaused ? 'paused' : 'running' }}
        >
          <div className="business-types-slider-set">{renderSet(0)}</div>
          <div className="business-types-slider-set" aria-hidden="true">
            {renderSet(1)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessTypesSlider;
